import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

/**
 * Thin banner making it obvious the data is demonstrative (DEMO).
 * Rendering policy:
 *   • Signed-in demo accounts (demo@, demo-de@)  → SHOW.
 *   • Everyone else (anon, real dealer, admin)   → HIDE.
 * The banner is intentionally NOT shown on public pages any more — it
 * only appears inside the dealer app for demo-mode users. Callers can
 * force it on with `alwaysShow` (kept for defensive symmetry — not
 * currently used anywhere).
 */
export default function DemoBanner({ variant = "amber", testid = "demo-banner", alwaysShow = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (!alwaysShow && !(user && user.is_demo_account === true)) return null;
  const styles =
    variant === "blue"
      ? {
          bar: "bg-blue-50 border-blue-100 text-blue-800",
          badge: "bg-blue-600 text-white",
        }
      : {
          bar: "bg-amber-50 border-amber-100 text-amber-800",
          badge: "bg-amber-500 text-white",
        };
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testid}
      className={`w-full rounded-xl border ${styles.bar} px-4 py-2 md:py-2.5 flex items-center gap-3 text-xs md:text-[13px] leading-tight`}
    >
      <span
        className={`inline-flex items-center justify-center rounded-full ${styles.badge} h-5 px-2 text-[10px] font-bold tracking-wider uppercase shrink-0`}
      >
        {t("demoBanner.badge")}
      </span>
      <div className="flex-1 min-w-0">
        <span className="font-semibold">{t("demoBanner.title")}</span>{" "}
        <span className="opacity-80">{t("demoBanner.body")}</span>
      </div>
      <AlertTriangle className="w-3.5 h-3.5 opacity-60 shrink-0 hidden sm:block" />
    </motion.div>
  );
}
